import { BackendServiceClient } from './BackendServiceClient';
import {
  GetFilteredFeedbackHistoryOutput,
  GetProductivityDataOutput,
  GetWeeklySpprSummariesOutput,
  GetWeeklySqprSummariesOutput,
  GetQualityEventsOutput,
  GetSQPRQualityErrorsOutput,
  GetProductivityReportOutput,
  GetQualityReportOutput,
  GetExpectedDPMOChangeEventsOutput,
  GetUnknownIdleTimeMetricsOutput,
  GetAuditReportOutput,
} from '../types/backendService';
import {
  getHourlyProductivityDataStub,
  getSpprSummariesStub,
  getDailyProductivityDataForWeekStub,
  getUnknownIdleTimeMetricsStub, getProductivityReportDataStub,
} from '../stubs/productivityStubs';
import {
  getExpectedDPMOChangeEventsStub,
  getSqprSummariesStub,
  getQualityErrorsStub,
  getQualityEventsStub, getQualityReportStub,
} from '../stubs/qualityStubs';
import { getFeedbacksStub } from '../stubs/feedbackStubs';
import { getAuditReportStub } from '../stubs/auditStubs';
import moment from 'moment';

import { MOCK_DATA_EMPLOYEE_IDS } from '../constants/featureGates';

/**
 * We want to vend fake data to admin users so that the UI can display data for them despite them not
 * actually having performance data. So for these users we return static data rather than retrieving
 * data from API calls.
 */
export class BackendServiceClientInterceptor extends BackendServiceClient {
  public async getFilteredFeedbackHistory(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    feedbackCategories?: string[]
  ): Promise<GetFilteredFeedbackHistoryOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getFeedbacksStub(feedbackCategories));
    } else {
      return super.getFilteredFeedbackHistory(userToken, employeeId, employeeLogin, feedbackCategories);
    }
  }

  public async getHourlyProductivityData(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
  ): Promise<GetProductivityDataOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getHourlyProductivityDataStub());
    } else {
      return super.getHourlyProductivityData(userToken, employeeId, employeeLogin);
    }
  }

  public async getDailyProductivityDataForWeek(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment
  ): Promise<GetProductivityDataOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getDailyProductivityDataForWeekStub());
    } else {
      return super.getDailyProductivityDataForWeek(userToken, employeeId, employeeLogin, weekEnd);
    }
  }

  public async getWeeklySpprSummaries(
    userToken: string,
    employeeId: string,
    employeeLogin: string
  ): Promise<GetWeeklySpprSummariesOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getSpprSummariesStub());
    } else {
      return super.getWeeklySpprSummaries(userToken, employeeId, employeeLogin);
    }
  }

  public async getQualityEvents(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment
  ): Promise<GetQualityEventsOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getQualityEventsStub());
    } else {
      return super.getQualityEvents(userToken, employeeId, employeeLogin, weekEnd);
    }
  }

  public async getWeeklySqprSummaries(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
  ): Promise<GetWeeklySqprSummariesOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getSqprSummariesStub());
    } else {
      return super.getWeeklySqprSummaries(userToken, employeeId, employeeLogin);
    }
  }

  public async getSQPRQualityErrors(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    weekEnd: moment.Moment
  ): Promise<GetSQPRQualityErrorsOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getQualityErrorsStub());
    } else {
      return super.getSQPRQualityErrors(userToken, employeeId, employeeLogin, weekEnd);
    }
  }

  public async getProductivityReport(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string
  ): Promise<GetProductivityReportOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getProductivityReportDataStub());
    } else {
      return super.getProductivityReport(userToken, employeeId, employeeLogin, warehouseId);
    }
  }

  public async getQualityReport(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string
  ): Promise<GetQualityReportOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getQualityReportStub());
    } else {
      return super.getQualityReport(userToken, employeeId, employeeLogin, warehouseId);
    }
  }

  public async getExpectedDPMOChangeEvents(
    userToken: string,
    employeeId: string,
    employeeLogin: string
  ): Promise<GetExpectedDPMOChangeEventsOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getExpectedDPMOChangeEventsStub());
    } else {
      return super.getExpectedDPMOChangeEvents(userToken, employeeId, employeeLogin);
    }
  }

  public async getUnknownIdleTimeMetrics(
    userToken: string,
    employeeId: string,
    employeeLogin: string,
    warehouseId: string
  ): Promise<GetUnknownIdleTimeMetricsOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getUnknownIdleTimeMetricsStub());
    } else {
      return super.getUnknownIdleTimeMetrics(userToken, employeeId, employeeLogin, warehouseId);
    }
  }

  public async getAuditReport(
    userToken: string,
    login: string,
    employeeId: string,
  ): Promise<GetAuditReportOutput> {
    if (this.isAdmin(employeeId)) {
      return Promise.resolve(getAuditReportStub());
    } else {
      return super.getAuditReport(userToken, login, employeeId);
    }
  }

  private isAdmin(employeeId: string): boolean {
    return MOCK_DATA_EMPLOYEE_IDS.includes(employeeId);
  }
}
